import { useAui, useAuiState } from "@assistant-ui/react";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import copyToClipboard from "copy-to-clipboard";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import type { TipTapBody } from "./tiptapToPlainText";
import { tiptapToPlainText } from "./tiptapToPlainText";

const COPIED_RESET_MS = 1500;

const readTiptapBody = (metadata: {
  custom?: unknown;
}): TipTapBody | undefined =>
  (metadata?.custom as { tiptap_body?: TipTapBody } | undefined)?.tiptap_body;

// Return undefined to fall back to the default resolution.
export type CopyTextResolver = (
  aui: ReturnType<typeof useAui>,
) => string | undefined;

interface CopyMessageActionProps {
  resolveText?: CopyTextResolver;
}

export const CopyMessageAction = ({ resolveText }: CopyMessageActionProps) => {
  const aui = useAui();
  const isCopied = useAuiState(s => s.message.isCopied);
  const isDisabled = useAuiState(
    s =>
      !s.message.parts.some(
        part => part.type === "text" && (part.text ?? "").length > 0,
      ) && !readTiptapBody(s.message.metadata),
  );
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (isCopied) setIsTooltipOpen(true);
  }, [isCopied]);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = setTimeout(
      () => aui.message.setIsCopied(false),
      COPIED_RESET_MS,
    );
    return () => clearTimeout(timeout);
  }, [isCopied, aui]);

  const handleCopy = () => {
    // The Tiptap body wins over the text part: a user turn's text part holds
    // the annotated form sent to the model, not what belongs on a clipboard.
    const tiptapBody = readTiptapBody(aui.message.getState().metadata);
    const text =
      resolveText?.(aui) ??
      (tiptapBody
        ? tiptapToPlainText(tiptapBody, tiptapBody.sgids_to_object_map ?? {})
        : aui.message.getCopyText());
    if (!text) return;
    copyToClipboard(text);
    aui.message.setIsCopied(true);
  };

  const handleOpenChange = (open: boolean) => {
    setIsTooltipOpen(open);
    if (!open && isCopied) {
      aui.message.setIsCopied(false);
    }
  };

  const label = isCopied
    ? t("ai_conversation.message_actions.copied")
    : t("ai_conversation.message_actions.copy");

  return (
    <Tooltip
      content={label}
      open={isTooltipOpen}
      onOpenChange={handleOpenChange}
      closeOnClick={false}
    >
      <IconButton
        variant="ghost"
        size="sm"
        icon={isCopied ? "checkmark" : "copy"}
        aria-label={label}
        onClick={handleCopy}
        disabled={isDisabled}
      />
    </Tooltip>
  );
};
